import { useMemo } from "react";
import { motion } from "framer-motion";
import { STORY, STORY_INTRO, STORY_OUTRO } from "@/lib/wedding";
import SmartImage from "@/components/SmartImage.jsx";
import Duet from "@/components/Duet.jsx";

/**
 * OurStory
 * -----------------------------------------------------------
 * Vertical timeline of chapters from wedding.js (STORY).
 * Chapters alternate left / right of a gold rail on md+, and stack on mobile.
 * A chapter shows a photo beside it only when it has an `image`.
 */
const FADE_LINE = {
  background: "linear-gradient(90deg, rgba(201,169,97,0) 0%, rgba(201,169,97,0.7) 50%, rgba(201,169,97,0) 100%)",
};

function Chapter({ c, right }) {
  return (
    <div className={`text-center ${right ? "md:text-left" : "md:text-right"}`}>
      <div className="inline-block text-[10px] uppercase tracking-chip text-gold-deep font-semibold mb-3">
        {c.date}
      </div>
      <h3 className="font-display text-3xl text-rani leading-tight">
        {c.title === "When Sona met Raj" ? (
          <>
            When <em className="italic text-gold-deep">Sona</em> met <em className="italic text-gold-deep">Raj</em>
          </>
        ) : (
          c.title
        )}
      </h3>
      {c.quote && (
        <p className="mt-3 font-display italic text-lg text-gold-deep leading-snug">“{c.quote}”</p>
      )}
      <p className="mt-4 font-body text-ink/80 leading-relaxed">{c.text}</p>
    </div>
  );
}

export default function OurStory() {
  return (
    <section id="journey" className="relative edge py-24 text-center overflow-hidden" style={{ background: "linear-gradient(180deg, #FAF6EE 0%, #F1E9D8 14%, #F1E9D8 86%, #FAF6EE 100%)" }}>
      <AutumnLeaves />

      <div className="relative z-10">
      <div className="overline text-rani mb-5">Our Journey</div>
      <h2 className="font-display text-4xl sm:text-6xl text-rani">How It All Began</h2>
      {/* our logo between two gold lines under the heading, as in the Alliance */}
      <div className="mt-5 flex items-center justify-center gap-3" aria-hidden="true">
        <span className="h-px w-20 sm:w-32" style={{ background: "linear-gradient(90deg, rgba(201,169,97,0) 0%, rgba(201,169,97,0.8) 100%)" }} />
        <img src="/brand/logo-wine.png" alt="" className="h-14 w-auto" draggable="false" />
        <span className="h-px w-20 sm:w-32" style={{ background: "linear-gradient(90deg, rgba(201,169,97,0.8) 0%, rgba(201,169,97,0) 100%)" }} />
      </div>

      {/* picture of the two of us, the same size and frame as the chapter photos */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto mt-10 w-full max-w-md"
      >
        <img
          src="/story/sona-met-raj.jpg"
          alt="Sonakshi and Shubham holding hands by Jal Mahal, Jaipur"
          className="w-full aspect-[1024/682] object-cover rounded-2xl border border-gold shadow-[0_20px_40px_-20px_rgba(58,10,24,0.35)]"
          loading="lazy"
          draggable="false"
        />
      </motion.div>
      {STORY_INTRO && (
        <p className="max-w-2xl mx-auto mt-10 font-body text-ink/80 leading-relaxed">
          {STORY_INTRO}
        </p>
      )}

      {/* plain gold line before the chapters */}
      <div className="mx-auto mt-10 mb-16 h-px w-40" style={FADE_LINE} />

      <div className="relative max-w-5xl mx-auto">
        {/* center rail (md+) */}
        <div
          className="hidden md:block absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2"
          style={{ background: "linear-gradient(180deg, rgba(201,169,97,0) 0%, rgba(201,169,97,0.7) 6%, rgba(201,169,97,0.7) 94%, rgba(201,169,97,0) 100%)" }}
        />

        <div className="grid gap-14 md:gap-16">
          {STORY.map((c, i) => {
            const right = i % 2 === 1; // text sits right of the rail on odd chapters
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="relative grid md:grid-cols-2 gap-8 md:gap-20 items-center"
              >
                <span className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-rani ring-4 ring-bone" />

                <div className={right ? "md:order-2" : ""}>
                  <Chapter c={c} right={right} />
                </div>

                <div className={right ? "md:order-1" : ""}>
                  {c.image ? (
                    <div className={`mx-auto w-full ${c.aspect === "3/2" ? "max-w-md" : "max-w-sm"}`}>
                      <SmartImage
                        src={c.image}
                        alt={c.title}
                        label={c.title}
                        aspect={c.aspect || "4/5"}
                        className="rounded-2xl border border-gold shadow-[0_20px_40px_-20px_rgba(58,10,24,0.35)]"
                      />
                    </div>
                  ) : (
                    <div className="hidden md:block" aria-hidden="true" />
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {STORY_OUTRO?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-20"
        >
          <div className="mx-auto mb-8 h-px w-40" style={FADE_LINE} />
          <p className="font-display italic text-3xl sm:text-4xl text-rani">{STORY_OUTRO[0]}</p>
          {STORY_OUTRO[1] && (
            <p className="mt-3 font-body text-sm uppercase tracking-chip text-gold-deep">{STORY_OUTRO[1]}</p>
          )}
          <div className="mx-auto max-w-md">
            <Duet />
          </div>
        </motion.div>
      )}
      </div>
    </section>
  );
}

/* ----------- Autumn leaves drifting down behind the timeline ----------- */
const LEAF_COLORS = ["#C2571A", "#D9822B", "#E8A33D", "#A63D1F", "#C9A961"];
// simple maple-style leaf, centred in a 24x24 box
const LEAF_PATH =
  "M12 1 L13.6 6.2 L18 4.4 L16.6 9.2 L22 9.6 L17.8 13 L20 17.6 L14.6 16.2 L12.6 22.5 L12 18.6 L11.4 22.5 L9.4 16.2 L4 17.6 L6.2 13 L2 9.6 L7.4 9.2 L6 4.4 L10.4 6.2 Z";

function AutumnLeaves({ count = 18 }) {
  const leaves = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        left: Math.random() * 100,
        size: 14 + Math.random() * 14,
        duration: 28 + Math.random() * 22, // the section is tall, so fall slowly
        delay: -Math.random() * 50,
        sway: (Math.random() < 0.5 ? -1 : 1) * (25 + Math.random() * 50),
        spin: 200 + Math.random() * 400,
        color: LEAF_COLORS[i % LEAF_COLORS.length],
        opacity: 0.35 + Math.random() * 0.3,
      })),
    [count]
  );
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden motion-reduce:hidden" aria-hidden="true">
      {leaves.map((l, i) => (
        <svg
          key={i}
          viewBox="0 0 24 24"
          className="absolute"
          style={{
            left: `${l.left}%`,
            width: l.size,
            height: l.size,
            opacity: l.opacity,
            "--sway": `${l.sway}px`,
            "--spin": `${l.spin}deg`,
            animation: `leaf-fall ${l.duration}s linear ${l.delay}s infinite`,
          }}
        >
          <path d={LEAF_PATH} fill={l.color} />
          <path d="M12 7 L12 22" stroke="rgba(90,30,10,0.35)" strokeWidth="0.6" />
        </svg>
      ))}
      <style>{`
        @keyframes leaf-fall {
          0%   { top: -4%;  transform: translateX(0) rotate(0deg); }
          25%  { transform: translateX(var(--sway)) rotate(calc(var(--spin) * 0.25)); }
          50%  { transform: translateX(0) rotate(calc(var(--spin) * 0.5)); }
          75%  { transform: translateX(calc(var(--sway) * -1)) rotate(calc(var(--spin) * 0.75)); }
          100% { top: 102%; transform: translateX(0) rotate(var(--spin)); }
        }
      `}</style>
    </div>
  );
}
